<section class="cg-admin-success-stories">
  <app-admin-page-header>
    <p description>
      Manage customer milestones and public transformation narratives. Homepage slider shows the first 4;
      all stories appear on the <a routerLink="/success-stories">Success Stories</a> page.
    </p>
    <ng-container actions>
      <app-admin-button variant="secondary" type="button" (click)="load()" [disabled]="loading()">
        Reload
      </app-admin-button>
      @if (showEdit()) {
        <app-admin-button variant="accent" type="button" (click)="save()" [disabled]="!canEdit() || saving() || loading()">
          {{ saving() ? 'Saving…' : 'Save Changes' }}
        </app-admin-button>
      }
    </ng-container>
  </app-admin-page-header>

  @if (loadError(); as le) {
    <div class="cg-admin-alert cg-admin-alert--warning">{{ le }}</div>
  }
  @if (error(); as err) {
    <div class="cg-admin-alert cg-admin-alert--error">{{ err }}</div>
  }

  @if (loading()) {
    <div class="cg-admin-grid">
      @for (i of [1, 2, 3]; track i) {
        <app-admin-skeleton variant="card" />
      }
    </div>
  } @else {
    <app-admin-card title="Section heading">
      <p class="cg-form-help cg-admin-success-stories__intro">
        Title and subtitle shown above the slider on the homepage and Success Stories page.
      </p>
      <div class="cg-admin-form-grid cg-admin-form-grid--2">
        <label class="cg-admin-field">
          <span class="cg-admin-field__label">Section title</span>
          <input class="cg-input" [(ngModel)]="form.successStoriesTitle" name="sst" />
        </label>
        <label class="cg-admin-field">
          <span class="cg-admin-field__label">Section subtitle</span>
          <input class="cg-input" [(ngModel)]="form.successStoriesSubtitle" name="ssst" />
        </label>
      </div>
    </app-admin-card>

    <div class="cg-admin-success-stories__repository">
      <header class="cg-admin-success-stories__repository-head">
        <div>
          <h2 class="cg-admin-success-stories__repository-title">Story Repository</h2>
          <p class="cg-admin-success-stories__repository-meta">
            {{ totalStories }} {{ totalStories === 1 ? 'STORY' : 'STORIES' }} TOTAL
            · {{ publishedCount }} PUBLISHED
          </p>
        </div>
        @if (showCreate()) {
          <app-admin-button variant="accent" type="button" (click)="openCreate()" [disabled]="!canCreate()">
            + Add story card
          </app-admin-button>
        }
      </header>

      <app-admin-table>
        <thead>
          <tr>
            <th>Thumbnail</th>
            <th>Label</th>
            <th>Title</th>
            <th>Story Summary</th>
            <th>CTA Label</th>
            <th style="text-align: right">Actions</th>
          </tr>
        </thead>
        <tbody>
          @if (totalStories === 0) {
            <tr>
              <td colspan="6">
                <app-admin-empty-state
                  icon="award"
                  title="No story cards yet"
                  description="Add your first success story to populate the homepage slider and Success Stories page."
                />
              </td>
            </tr>
          }
          @for (story of paginatedStories; track story.key) {
            <tr>
              <td>
                @if (successStoryPreview(story.key); as previewUrl) {
                  <div class="cg-admin-success-stories__thumb">
                    <img [src]="previewUrl" alt="" />
                  </div>
                } @else {
                  <div class="cg-admin-success-stories__thumb cg-admin-success-stories__thumb--empty" aria-hidden="true"></div>
                }
              </td>
              <td>
                @if (story.pill.trim()) {
                  <span class="cg-admin-success-stories__pill">{{ story.pill }}</span>
                } @else {
                  <span class="cg-form-help">—</span>
                }
              </td>
              <td>
                <strong class="cg-admin-success-stories__title-cell">{{ storyTitlePreview(story.title) }}</strong>
                @if (!isPublished(story)) {
                  <span class="cg-admin-success-stories__draft-tag">Draft</span>
                }
              </td>
              <td>
                <p class="cg-admin-success-stories__summary-cell">
                  {{ storySummaryPreview(story.description) || '—' }}
                </p>
              </td>
              <td>{{ story.ctaLabel.trim() || '—' }}</td>
              <td>
                <div class="cg-admin-table-actions">
                  @if (showEdit()) {
                    <app-admin-button variant="ghost" type="button" (click)="openEdit(story)" [disabled]="!canEdit()">
                      Edit
                    </app-admin-button>
                  }
                  @if (showDelete()) {
                    <app-admin-button variant="danger" type="button" (click)="removeSuccessStory(story.key)" [disabled]="!canDelete()">
                      Delete
                    </app-admin-button>
                  }
                </div>
              </td>
            </tr>
          }
        </tbody>
      </app-admin-table>

      @if (totalStories > 0) {
        <footer class="cg-admin-success-stories__pagination">
          <span class="cg-admin-success-stories__pagination-label">{{ paginationLabel }}</span>
          <div class="cg-admin-success-stories__pagination-controls">
            <app-admin-button
              variant="secondary"
              type="button"
              (click)="prevPage()"
              [disabled]="!canPrevPage"
              aria-label="Previous page"
            >
              ‹
            </app-admin-button>
            <app-admin-button
              variant="secondary"
              type="button"
              (click)="nextPage()"
              [disabled]="!canNextPage"
              aria-label="Next page"
            >
              ›
            </app-admin-button>
          </div>
        </footer>
      }
    </div>
  }

  @if (showModal()) {
    <app-admin-modal
      [title]="editingKey() ? 'Edit story card' : 'Add story card'"
      size="lg"
      (closed)="closeModal()"
    >
      <div class="cg-admin-form-grid cg-admin-form-grid--2">
        <label class="cg-admin-field">
          <span class="cg-admin-field__label">Pill label</span>
          <input class="cg-input" [(ngModel)]="modalDraft.pill" name="modalPill" />
        </label>
        <label class="cg-admin-field cg-admin-field--full">
          <span class="cg-admin-field__label">Title (use new lines for line breaks)</span>
          <textarea class="cg-textarea" rows="2" [(ngModel)]="modalDraft.title" name="modalTitle"></textarea>
        </label>
        <label class="cg-admin-field cg-admin-field--full">
          <span class="cg-admin-field__label">Story summary</span>
          <textarea class="cg-textarea" rows="4" [(ngModel)]="modalDraft.description" name="modalDesc"></textarea>
        </label>
        <label class="cg-admin-field">
          <span class="cg-admin-field__label">CTA button label</span>
          <input class="cg-input" [(ngModel)]="modalDraft.ctaLabel" name="modalCta" />
        </label>
        <label class="cg-admin-field">
          <span class="cg-admin-field__label">CTA button URL</span>
          <input
            class="cg-input"
            [(ngModel)]="modalDraft.ctaHref"
            name="modalHref"
            placeholder="#cta"
          />
          <span class="cg-form-help">
            Leave empty to link to this story on the Success Stories page. Use <code>#cta</code> for the home consultation form.
          </span>
        </label>
        <div class="cg-admin-field cg-admin-field--full">
          <span class="cg-admin-field__label">Image</span>
          @if (successStoryPreview(modalDraft.key); as previewUrl) {
            <div class="cg-admin-story-card__preview cg-admin-success-stories__modal-preview">
              <img [src]="previewUrl" alt="Story preview" />
            </div>
          }
          <input
            type="file"
            class="cg-input"
            accept="image/*"
            (change)="onSuccessStoryFile(modalDraft.key, $event)"
          />
          <div class="cg-admin-story-card__upload">
            @if (showUpload()) {
              <app-admin-button
                variant="accent"
                type="button"
                (click)="uploadSuccessStoryImage(modalDraft.key)"
                [disabled]="!hasSuccessStoryFile(modalDraft.key) || successStoryUploadingKey() === modalDraft.key"
              >
                {{ successStoryUploadingKey() === modalDraft.key ? 'Uploading…' : 'Upload image' }}
              </app-admin-button>
            }
          </div>
          @if (successStoryMessage(); as msg) {
            <p class="cg-form-help">{{ msg }}</p>
          }
        </div>
      </div>

      @if (modalError(); as msg) {
        <p class="cg-admin-alert cg-admin-alert--error" style="margin-top: var(--cg-space-4)">{{ msg }}</p>
      }

      <div class="cg-admin-page-header__actions cg-admin-success-stories__modal-actions">
        <app-admin-button variant="cancel" type="button" (click)="closeModal()">Cancel</app-admin-button>
        @if (showSaveModal()) {
          <app-admin-button variant="accent" type="button" (click)="saveModal()" [disabled]="modalSaving()">
            {{ modalSaving() ? 'Saving…' : 'Save story' }}
          </app-admin-button>
        }
      </div>
    </app-admin-modal>
  }
</section>
